import { ChangeEvent, JSX, PureComponent } from "react";
import { QESButton, QESTextInput, QESSelect, QESCheckbox, QESRadioButton, QESAlert } from "@app/shared/qes-standards";
import { LoadingSpinner, TooltipWrapper } from "@app/shared";
import { ClientListItem } from "@app/client";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import React from "react";
import { cloneDeep } from "lodash";
import { AccessCriteriaModel, AdequacySpecialtyGroupAnalysisPropertiesEnum, AdequacySpecialtyGroupDisplayPropertiesEnum, EntityTypeEnum, processKeyDownNumericValidation, processKeyDownTextValidation, ServicingCriteriaModel, SpecialtyGroupModel, TemplateDetailsData } from "../manage-specialties/specialty";
import { CalculationMethodEnum, ProviderRegionTypeEnum, ServicingAnalysisTypeEnum, ServicingCountTypeEnum, ServicingMinimumTypeEnum } from "@app/templates/templateModels";
import { CodeMapSetService } from "../../codemaps";
import { CodeMapTypeEnum } from "../../codemaps/CodeMapSetModels";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "../../shared/waffles";
import { InputValidationRegExps } from "../../projects/enumerators";
import { Features } from "@app/feature-flags";

/* eslint-disable sort-keys */

interface CreatePanelProps {
  currentUserid: number;
  defaultClient: ClientListItem;
  features: Set<string>;
  handleSave: (
    accessReqInCounty: boolean,
    categoryDisplayName: string,
    creditPercentage: number,
    expandable: boolean,
    isProvider: boolean,
    loading: boolean,
    informational: boolean,
    selectedTaxonomyCrosswalk: string,
    servicingReqInCounty: boolean,
    specialtyAccessCriteria: AccessCriteriaModel[],
    specialtyServicingCriteria: ServicingCriteriaModel[],
    specialtyCode: string,
    specialtyCodeLineItems: SpecialtyCodeLineItem[],
    specialtyCodeName: string,
    specialtyCodeProviderWeight: string,
    specialtyGroupCode: string,
    specialtyGroupCodeName: string,
    taxonomyCode: string,
    taxonomyCrosswalks: JSX.Element[],
    taxonomySpecialtyCode: string,
    taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[],
    telehealthCredit: boolean,
    usingRatios: boolean,
    countDistinct: ServicingCountTypeEnum,
    minimumServicingCounts: number,
    providerCount: number,
    index?: number
  ) => void;
  allowClientEdit: boolean;
  loading: boolean;
  templateDetails: TemplateDetailsData;
  specialtyGroup?: SpecialtyGroupModel;
  countyClassificationMap: number;
  index?: number,
}

interface CreatePanelState {
  accessReqInCounty: boolean;
  templateDetails: TemplateDetailsData;
  categoryDisplayName: string;
  creditPercentage: number;
  creditPercentageIsValid: boolean;
  expandable: boolean;
  isProvider: boolean;
  loading: boolean;
  nameIsValid: boolean;
  nameIsValidText: string;
  informational: boolean;
  selectedTaxonomyCrosswalk: string;
  countyClassificationMap: number;
  servicingReqInCounty: boolean;
  skipAccessTimeAndDistanceCalc: boolean;
  specialtyCode: string,
  specialtyCodeLineItems: SpecialtyCodeLineItem[],
  specialtyCodeName: string;
  specialtyCodeProviderWeight: string;
  specialtyCodeProviderWeightIsValid: boolean;
  specialtyGroupCode: string;
  specialtyGroupCodeIsValid: boolean;
  specialtyGroupCodeName: string;
  specialtyGroupCodeNameIsValid: boolean;
  taxonomyCode: string;
  taxonomyCrosswalks: JSX.Element[];
  taxonomySpecialtyCode: string;
  taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[];
  telehealthCredit: boolean;
  usingRatios: boolean;

  countDistinct: ServicingCountTypeEnum;
  minimumServicingCounts: string | number;
  providerCount: number;
  servicingDetailsData: ServicingDetailsRow[];
  specialtyAccessCriteria: ServicingDetailsModel[];
  accessCustomizationsData: ServicingDetailsRow[];
  accessCustomizations: ServicingDetailsModel[];
  counties: string[];
  memberCounts: string | number;
  panelIsValid: boolean;
  servicingDetailsErrorMessages: string[];
  highlightErrors: boolean;
  duplicateSpecialtyCodeLineItem: boolean;
}

interface SpecialtyCodeLineItem {
  id: number,
  specialtyCode: string,
  specialtyCodeName: string,
  specialtyCodeProviderWeight: string,
}

interface TaxonomySpecialtyCodeLineItem {
  id: number,
  taxonomyCode: string,
  taxonomySpecialtyCode: string,
}

interface CalculationMethodDropdown {
  value: CalculationMethodEnum;
  displayText: string;
}

interface ServicingDetailsModel {
  geographyId: string;
  maximumDistanceAllowed: number;
  maximumTimeAllowed: number;
  minimumProviderCount: number;
  passPercentage: number;

  CountyIsValid: boolean;
  MinimumProviderCountIsValid: boolean;
  MaximumDistanceAllowedIsValid: boolean;
  MaximumTimeAllowedIsValid: boolean;
  MemberCountIsValid: boolean;
  PassPercentageIsValid: boolean;
  ServicingMinimumCountIsValid: boolean;
  servicingMinimumCount: string | number;
  servicingRatio: string;
  calculationMethod: CalculationMethodEnum;
  memberCounts: string | number;
}

interface ServicingDetailsRow {
  geographyId: JSX.Element;
  requiredProviders: JSX.Element;
  miles: JSX.Element;
  minutes: JSX.Element;
  passPercentage: JSX.Element;
  servicingProviders: JSX.Element;
  calculationMethod: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const calculationMethodDropdownValues: CalculationMethodDropdown[] = Object.values(CalculationMethodEnum).map((x) => {
  let displayText = "";
  let value: CalculationMethodEnum;
  switch (x) {
    case CalculationMethodEnum.TimeAndDistance:
      displayText = "Time And Distance";
      value = CalculationMethodEnum.TimeAndDistance;
      break;
    case CalculationMethodEnum.TimeOrDistance:
      displayText = "Time Or Distance";
      value = CalculationMethodEnum.TimeOrDistance;
      break;
    case CalculationMethodEnum.Time:
      displayText = "Time";
      value = CalculationMethodEnum.Time;
      break;
    case CalculationMethodEnum.Distance:
      displayText = "Distance";
      value = CalculationMethodEnum.Distance;
      break;
    default:
      displayText = "";
      value = CalculationMethodEnum.Unknown;
      break;
  }
  return { value: value, displayText: displayText }
});
class WrappedCreateNewSpecialtyPanelContent extends PureComponent<CreatePanelProps & FlyoutContextOutgoingProps, CreatePanelState> {
  private nameValidationInterval: number = null;

  constructor(props: CreatePanelProps & FlyoutContextOutgoingProps) {
    super(props);
    const defaultSpecialtyGroupInfo = {
      accessReqInCounty: false,
      creditPercentage: 10,
      expandable: false,
      isProvider: false,
      informational: false,
      selectedTaxonomyCrosswalk: '',
      servicingReqInCounty: false,
      skipAccessTimeAndDistanceCalc: false,
      specialtyCode: "",
      specialtyCodeName: "",
      specialtyCodeProviderWeight: "1",
      specialtyGroupCode: "",
      specialtyGroupCodeIsValid: true,
      specialtyGroupCodeName: "",
      specialtyGroupCodeNameIsValid: true,
      telehealthCredit: false,
      usingRatios: false,

      calculationMethod: "",
      urbanCalculationMethod: "",
      suburbanCalculationMethod: "",
      ruralCalculationMethod: "",
      servicingProviders: 0,
      countDistinct: "",
      miles: 0,
      minutes: 0,
      minimumServicingCounts: 0,
      passPercentage: 0,
      providerCount: 0,
      requiredProviders: 0,
    }
    if (props.specialtyGroup === undefined) {
      this.state = {
        accessReqInCounty: defaultSpecialtyGroupInfo.accessReqInCounty,
        templateDetails: this.props.templateDetails,
        categoryDisplayName: "",
        creditPercentage: defaultSpecialtyGroupInfo.creditPercentage,
        creditPercentageIsValid: false,
        expandable: defaultSpecialtyGroupInfo.expandable,
        isProvider: defaultSpecialtyGroupInfo.isProvider,
        loading: false,
        nameIsValid: defaultSpecialtyGroupInfo.specialtyGroupCode.length > 0,
        nameIsValidText: "",
        informational: defaultSpecialtyGroupInfo.informational,
        selectedTaxonomyCrosswalk: defaultSpecialtyGroupInfo.selectedTaxonomyCrosswalk,
        countyClassificationMap: this.props.countyClassificationMap,
        servicingReqInCounty: defaultSpecialtyGroupInfo.servicingReqInCounty,
        skipAccessTimeAndDistanceCalc: defaultSpecialtyGroupInfo.skipAccessTimeAndDistanceCalc,
        specialtyCode: defaultSpecialtyGroupInfo.specialtyCode,
        specialtyCodeLineItems: [],
        specialtyCodeName: defaultSpecialtyGroupInfo.specialtyCodeName,
        specialtyCodeProviderWeight: defaultSpecialtyGroupInfo.specialtyCodeProviderWeight,
        specialtyCodeProviderWeightIsValid: true,
        specialtyGroupCode: defaultSpecialtyGroupInfo.specialtyGroupCode,
        specialtyGroupCodeIsValid: defaultSpecialtyGroupInfo.specialtyGroupCodeIsValid,
        specialtyGroupCodeName: defaultSpecialtyGroupInfo.specialtyGroupCodeName,
        specialtyGroupCodeNameIsValid: true,
        taxonomyCode: "",
        taxonomyCrosswalks: [],
        taxonomySpecialtyCode: "",
        taxonomySpecialtyCodeLineItems: [],
        telehealthCredit: defaultSpecialtyGroupInfo.telehealthCredit,
        usingRatios: defaultSpecialtyGroupInfo.usingRatios,

        countDistinct: defaultSpecialtyGroupInfo.countDistinct as ServicingCountTypeEnum,
        minimumServicingCounts: defaultSpecialtyGroupInfo.minimumServicingCounts,
        providerCount: defaultSpecialtyGroupInfo.providerCount,
        servicingDetailsData: [],
        specialtyAccessCriteria: [],
        accessCustomizationsData: [],
        accessCustomizations: [],
        counties: [],
        servicingDetailsErrorMessages: [],
        memberCounts: 0,
        panelIsValid: false,
        highlightErrors: false,
        duplicateSpecialtyCodeLineItem: false,
      };
    } else {
      const specialtyCodeLineItems: SpecialtyCodeLineItem[] = props.specialtyGroup.specialties.map((x) => (
        {
          id: x.displayOrder,
          specialtyCode: x.code,
          specialtyCodeName: x.name,
          specialtyCodeProviderWeight: x.weight
        }
      ))

      this.state = {
        accessReqInCounty: props.specialtyGroup.accessCriteria[0].providerRegionType == ProviderRegionTypeEnum.InRegion ? true : false,
        templateDetails: this.props.templateDetails,
        categoryDisplayName: "",
        creditPercentage: this.props.specialtyGroup.accessCriteria[0].telehealthAccessCreditPercentage,
        creditPercentageIsValid: false,
        expandable: this.props.specialtyGroup.displayProperties == AdequacySpecialtyGroupDisplayPropertiesEnum.CanExpandSpecialties ? true : false,
        isProvider: this.props.specialtyGroup.entityType == EntityTypeEnum.Provider ? true : false,
        loading: false,
        nameIsValid: props.specialtyGroup.code.length > 0,
        nameIsValidText: "",
        informational: this.props.specialtyGroup.analysisProperties == AdequacySpecialtyGroupAnalysisPropertiesEnum.InformationalOnly ? true : false,
        selectedTaxonomyCrosswalk: this.props.templateDetails?.attestationCrosswalkCodeMapSetId.toString(),
        countyClassificationMap: this.props.countyClassificationMap,
        servicingReqInCounty: this.props.specialtyGroup.servicingCriteria[0]?.servicingAnalysisType == ServicingAnalysisTypeEnum.InRegion ? true : false,
        skipAccessTimeAndDistanceCalc: this.props.specialtyGroup.accessCriteria[0].skipAccessTimeAndDistanceCalculations,
        specialtyCode: defaultSpecialtyGroupInfo.specialtyCode, // ?
        specialtyCodeLineItems: specialtyCodeLineItems,
        specialtyCodeName: '',
        specialtyCodeProviderWeight: defaultSpecialtyGroupInfo.specialtyCodeProviderWeight, // ?
        specialtyCodeProviderWeightIsValid: true,
        specialtyGroupCode: props.specialtyGroup.code,
        specialtyGroupCodeIsValid: true,
        specialtyGroupCodeName: props.specialtyGroup.name,
        specialtyGroupCodeNameIsValid: true,
        taxonomyCode: "",
        taxonomyCrosswalks: [],
        taxonomySpecialtyCode: "",
        taxonomySpecialtyCodeLineItems: [],
        telehealthCredit: this.props.specialtyGroup.accessCriteria[0].isTelehealthAccessCreditCustomization,
        usingRatios: this.props.specialtyGroup.servicingMinimumType === ServicingMinimumTypeEnum.Ratio ? true : false,
        countDistinct: this.props.specialtyGroup.servicingCountBy as ServicingCountTypeEnum,
        minimumServicingCounts: this.props.specialtyGroup.servicingCriteria[0].servicingMinimumCount ?? null,
        providerCount: defaultSpecialtyGroupInfo.providerCount,
        servicingDetailsData: [],
        specialtyAccessCriteria: [],
        accessCustomizationsData: [],
        accessCustomizations: [],
        counties: [],
        memberCounts: 0,
        servicingDetailsErrorMessages: [],
        panelIsValid: false,
        highlightErrors: false,
        duplicateSpecialtyCodeLineItem: false,
      };
    }
  }
  /* eslint-enable sort-keys */

  public async componentDidMount() {
    this.setState({ loading: true });

    const specialtyGroupsData: SpecialtyGroupModel[] = this.props.templateDetails?.categories?.flatMap(sg => sg.specialtyGroups);
    const servicingDetailsGeographyIds =
      [...new Set(
        [...specialtyGroupsData[0]?.accessCriteria?.map(x => x.geographyId),
        ...specialtyGroupsData[0]?.servicingCriteria?.map(x => x.geographyId)]
      )];

    const codeMapSetService = new CodeMapSetService();
    const userCodeMaps = await codeMapSetService.get(CodeMapTypeEnum.SpecialtyCodeMap);
    this.setState({
      taxonomyCrosswalks: userCodeMaps?.map(cm => {
        return (<option key={cm.codeMapSetId} value={cm.resource.resourceId}>{cm.resource.name}</option>)
      })
    });

    const accessCodeMaps = (await codeMapSetService.getCodeMapItems(this.props.countyClassificationMap))
      .map(cmi => cmi.codeMapValue)
      .filter((v, i, s) => s.indexOf(v) === i);

    this.setState({
      counties: servicingDetailsGeographyIds.filter((x) => !accessCodeMaps.includes(x)),
      specialtyAccessCriteria: await this.getDefaultAccessTableData(accessCodeMaps),

    }, () => {
      this.createServicingDetailsWafflesTable();
      this.createCustomizationsWafflesTable();
    });

    this.setState({
      loading: false,
    });
  }

  private handleCategoryChange = async (e: string) => {
    this.setState({
      categoryDisplayName: e
    });
  }

  private getDefaultAccessTableData = async (defaultAccessCriteria: string[]): Promise<ServicingDetailsModel[]> => {
    const accessCustomizationTableData = defaultAccessCriteria.map((x) => {
      const sd: ServicingDetailsModel = {
        calculationMethod: CalculationMethodEnum.Unknown,
        geographyId: x,
        // eslint-disable-next-line sort-keys
        CountyIsValid: true,
        MaximumDistanceAllowedIsValid: false,
        MaximumTimeAllowedIsValid: false,
        MemberCountIsValid: false,
        MinimumProviderCountIsValid: false,
        PassPercentageIsValid: false,
        ServicingMinimumCountIsValid: true,
        maximumDistanceAllowed: 0,
        maximumTimeAllowed: 0,
        memberCounts: 0,
        minimumProviderCount: 0,
        passPercentage: 0,
        servicingMinimumCount: 0,
        servicingRatio: "0:0",
      };
      return sd;
    });
    return accessCustomizationTableData;
  }

  private handleAddAccessCustomization = async () => {
    const accessCustomizations = this.state.accessCustomizations;
    const emptyAccessCustomization: ServicingDetailsModel = {
        calculationMethod: CalculationMethodEnum.Unknown,
        geographyId: '',
        // eslint-disable-next-line sort-keys
        CountyIsValid: true,
        MaximumDistanceAllowedIsValid: false,
        MaximumTimeAllowedIsValid: false,
        MemberCountIsValid: false,
        MinimumProviderCountIsValid: false,
        PassPercentageIsValid: false,
        ServicingMinimumCountIsValid: true,
        maximumDistanceAllowed: 0,
        maximumTimeAllowed: 0,
        memberCounts: 0,
        minimumProviderCount: 0,
        passPercentage: 0,
        servicingMinimumCount: 0,
        servicingRatio: "0:0",
    }
    accessCustomizations.push(emptyAccessCustomization);
    this.setState({
      accessCustomizations: accessCustomizations
    }, () => this.createCustomizationsWafflesTable(accessCustomizations));
  }

  private handleTaxonomyCrosswalkChange = async (e: string) => {
    this.setState({
      selectedTaxonomyCrosswalk: e
    });
  }

  private handleMinimumProviderCountChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value: string = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2))) {
      const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).minimumProviderCount = parseInt(value, 10);
      if (customization)
        this.setState({ accessCustomizations: newAccessCriteriaValues }, () => this.createCustomizationsWafflesTable(newAccessCriteriaValues));
      else
        this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => this.createServicingDetailsWafflesTable(newAccessCriteriaValues));

      this.handleValidateMinimumProviderCount(value, geographyId, customization);

    } else {
      this.handleValidateMinimumProviderCount(value, geographyId, customization);
      e.preventDefault();
    }
  }

  private handleCountyChange = async (e: ChangeEvent<HTMLSelectElement>, priorGeoId: string) => {
    const newAccessCriteriaValues = this.state.accessCustomizations;

    newAccessCriteriaValues.find(x => x.geographyId === priorGeoId).geographyId = e.target.value;
    this.setState({ accessCustomizations: newAccessCriteriaValues }, () => this.createCustomizationsWafflesTable(newAccessCriteriaValues));
  }

  private handleMaximumDistanceAllowedChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow4Digits))) {
      const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).maximumDistanceAllowed = parseInt(value, 10);
      if (customization)
        this.setState({ accessCustomizations: newAccessCriteriaValues });
      else
        this.setState({ specialtyAccessCriteria: newAccessCriteriaValues });

      this.handleValidateMaximumDistanceAllowed(value, geographyId, customization);

    } else {
      this.handleValidateMaximumDistanceAllowed(value, geographyId, customization, false);
      e.preventDefault();
    }
  }

  private handleMaximumTimeAllowedChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow4Digits))) {
      const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).maximumTimeAllowed = parseInt(value, 10);
      if (customization)
        this.setState({ accessCustomizations: newAccessCriteriaValues });
      else
        this.setState({ specialtyAccessCriteria: newAccessCriteriaValues });

      this.handleValidateMaximumTimeAlowed(value, geographyId, customization);

    } else {
      this.handleValidateMaximumTimeAlowed(value, geographyId, customization, false);
      e.preventDefault();
    }
  }

  private handlePassPercentageChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100))) {
      const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).passPercentage = parseInt(value, 10);
      if (customization)
        this.setState({ accessCustomizations: newAccessCriteriaValues });
      else
        this.setState({ specialtyAccessCriteria: newAccessCriteriaValues });

      this.handleValidatePassPercentage(value, geographyId, customization);

    } else {
      this.handleValidatePassPercentage(value, geographyId, customization, false);
      e.preventDefault();
    }
  }

  private handleServicingMinimumCountChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value: string = e.target.value;
    if (isNaN(parseFloat(value))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (this.state.usingRatios === false) {
      if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowDigitsNoLimit))) {
        const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingMinimumCount = parseInt(value, 10);
        if (customization)
          this.setState({ accessCustomizations: newAccessCriteriaValues });
        else
          this.setState({ specialtyAccessCriteria: newAccessCriteriaValues });

        this.handleValidateMinimumServicingProviderCount(value, geographyId, customization);

      } else {
        this.handleValidateMinimumServicingProviderCount(value, geographyId, customization, false);
        e.preventDefault();
      }
    } else {
      if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod))) {
        const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
        const currentRow = newAccessCriteriaValues.find(x => x.geographyId === geographyId);
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingMinimumCount = value;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingRatio = this.state.usingRatios ? value + ":" + currentRow.memberCounts : "0:0";
        if (customization)
          this.setState({ accessCustomizations: newAccessCriteriaValues });
        else
          this.setState({ specialtyAccessCriteria: newAccessCriteriaValues });

        this.handleValidateMinimumServicingProviderCount(value, geographyId, customization);
      } else {
        this.handleValidateMinimumServicingProviderCount(value, geographyId, customization, false);
        e.preventDefault();
      }
    }
  }

  private handleCreditPercentageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    let value: string = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100))) {
      this.setState({ creditPercentage: parseInt(value, 10) });

      this.handleValidateCreditPercentage(value);

    } else {
      this.handleValidateCreditPercentage(value, false);
      e.preventDefault();
    }
  }

  private handleMembersCountChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value: string = e.target.value;
    if (isNaN(parseFloat(value))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    const currentRow = newAccessCriteriaValues.find(x => x.geographyId === geographyId);
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).memberCounts = value;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingRatio = currentRow.servicingMinimumCount + ":" + value;
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => { this.handleValidateMemberCount(value, geographyId, customization); });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => { this.handleValidateMemberCount(value, geographyId, customization); });
  }

  private handleCalculationMethodChange = async (e: CalculationMethodEnum, geographyId: string, customization: boolean) => {
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).calculationMethod = e;
    if (customization) {
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => this.createCustomizationsWafflesTable(newAccessCriteriaValues));
    }
    else {
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => this.createServicingDetailsWafflesTable(newAccessCriteriaValues));
    }
  }

  private handleRemoveAccessCustomization = async (geographyId: string) => {
    const filteredAccessCustomizations = this.state.accessCustomizations.filter(x => x.geographyId !== geographyId);
    this.setState({ accessCustomizations: filteredAccessCustomizations }, () => this.createCustomizationsWafflesTable(filteredAccessCustomizations));
  }

  private handleSpecialtyGroupCodeChange = async (e: string) => {
    const specialtyGroupCodes: string[] = this.props.templateDetails.categories.flatMap(cat => cat.specialtyGroups).flatMap(sg => sg.code);
    this.setState({
      specialtyGroupCode: e,
      specialtyGroupCodeIsValid: !specialtyGroupCodes.some(code => code.toLowerCase() === e.toLowerCase()) && e.length > 0,
    });
    this.validateName();
  }

  private handleSpecialtyGroupNameChange = async (e: string) => {
    const specialtyGroupNames: string[] = this.props.templateDetails.categories.flatMap(cat => cat.specialtyGroups).flatMap(sg => sg.name);
    this.setState({
      specialtyGroupCodeName: e,
      specialtyGroupCodeNameIsValid: !specialtyGroupNames.some(name => name?.toLowerCase() === e.toLowerCase()) && e.length > 0,
    });
    this.validateName();
  }

  private handleValidateMinimumProviderCount = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) == 1 || parseInt(value, 10) == 2;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MinimumProviderCountIsValid = isvalid;
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues });
  }

  private handleValidateMaximumDistanceAllowed = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) >= 1 && parseInt(value, 10) < 10000;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MaximumDistanceAllowedIsValid = isvalid;
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => { this.createCustomizationsWafflesTable(); });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => { this.createServicingDetailsWafflesTable(); });
  }

  private handleValidateMaximumTimeAlowed = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) >= 1 && parseInt(value, 10) < 10000;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MaximumTimeAllowedIsValid = isvalid;
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => { this.createCustomizationsWafflesTable(); });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => { this.createServicingDetailsWafflesTable(); });
  }

  private handleValidatePassPercentage = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = (this.state.accessReqInCounty || this.state.skipAccessTimeAndDistanceCalc) || parseInt(value, 10) <= 100 && parseInt(value, 10) > 0;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).PassPercentageIsValid = isvalid;
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => { this.createCustomizationsWafflesTable() });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => { this.createServicingDetailsWafflesTable() });
  }

  private handleValidateMinimumServicingProviderCount = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = !isNaN(parseFloat(value));
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    if (this.state.usingRatios) {
      if (customization) {
        isvalid = +value <= +this.state.accessCustomizations.find(x => x.geographyId === geographyId).memberCounts;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isvalid;
      } else {
        isvalid = +value <= +this.state.specialtyAccessCriteria.find(x => x.geographyId === geographyId).memberCounts;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isvalid;
      }
    }
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).ServicingMinimumCountIsValid = isvalid;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isvalid;
    
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => { this.createCustomizationsWafflesTable(); });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => { this.createServicingDetailsWafflesTable(); });
  }

  private handleValidateMemberCount = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isValid = +value >= (customization
      ? +this.state.accessCustomizations.find(x => x.geographyId === geographyId).servicingMinimumCount
      : +this.state.specialtyAccessCriteria.find(x => x.geographyId === geographyId).servicingMinimumCount);

    if (typeof setIsValidTo != 'undefined') {
      isValid = setIsValidTo;
    }

    const newAccessCriteriaValues = customization ? this.state.accessCustomizations : this.state.specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isValid;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).ServicingMinimumCountIsValid = isValid;
    if (customization)
      this.setState({ accessCustomizations: newAccessCriteriaValues }, () => { this.createCustomizationsWafflesTable(); });
    else
      this.setState({ specialtyAccessCriteria: newAccessCriteriaValues }, () => { this.createServicingDetailsWafflesTable(); });
  }

  private handleValidateSpecialtyCodeProviderWeight = async (value: string, setIsValidTo?: boolean) => {

    if (!isNaN(parseFloat(value))) {
      let isvalid = parseFloat(value) <= 1;
      if (typeof setIsValidTo != 'undefined') {
        isvalid = setIsValidTo;
      }
      let specCodeProvWeightIsValid = this.state.specialtyCodeProviderWeightIsValid;
      specCodeProvWeightIsValid = isvalid;
      this.setState({ specialtyCodeProviderWeightIsValid: specCodeProvWeightIsValid });
    }
  }

  private handleSpecialtyCodeProviderWeightChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (this.execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowDigitsLessThanEqual1AndAllowDecimalPlace))) {
      this.setState({ specialtyCodeProviderWeight: value });

      this.handleValidateSpecialtyCodeProviderWeight(value);

    } else {
      this.handleValidateSpecialtyCodeProviderWeight(value, false);
      e.preventDefault();
    }
  }

  private handleValidateCreditPercentage = async (value: string, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) <= 100;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    let specCodeProvWeightIsValid = this.state.creditPercentageIsValid;
    specCodeProvWeightIsValid = isvalid;
    this.setState({ creditPercentageIsValid: specCodeProvWeightIsValid });
  }

  private execRegex(value: string, regexToExecute: RegExp, allowEmptyString: boolean = true): boolean {
    if (allowEmptyString === true && value.length === 0) {
      return true;
    }
    const ret = regexToExecute.exec(value);
    if (ret) {
      return ret.length > 0;
    } else {
      return false;
    }
  }

  public componentWillUnmount() {
    window.clearInterval(this.nameValidationInterval);
  }

  private validateName = async () => {
    window.clearInterval(this.nameValidationInterval);
  }

  private handleAddClick = () => {
    this.validatePanel();
    if (!this.state.panelIsValid) {
      this.setState({ highlightErrors: true }, () => { this.createServicingDetailsWafflesTable(); this.createCustomizationsWafflesTable() });
      return;
    }

    const totalCriteria = this.state.specialtyAccessCriteria.concat(this.state.accessCustomizations);
    this.setState({ specialtyAccessCriteria: totalCriteria });

    const accessCriteria: AccessCriteriaModel[] = [];
    const servicingCriteria: ServicingCriteriaModel[] = [];
    totalCriteria.forEach(newCriteria => {
      accessCriteria.push({
          calculationMethod: newCriteria.calculationMethod,
          cmsApplicationCreditPercentage: 0,
          conAccessCreditPercentage: 0,
          geographyId: newCriteria.geographyId,
          isAccessCustomization: this.state.counties.includes(newCriteria.geographyId),
          isTelehealthAccessCreditCustomization: this.state.telehealthCredit,
          isValid: true,
          maximumDistanceAllowed: newCriteria.maximumDistanceAllowed,
          maximumTimeAllowed: newCriteria.maximumTimeAllowed,
          minimumProviderCount: newCriteria.minimumProviderCount,
          passPercentage: newCriteria.passPercentage,
          providerRegionType: this.state.accessReqInCounty ? ProviderRegionTypeEnum.InRegion : ProviderRegionTypeEnum.InRegionOrCoversMember,
          servicingCountMethodology: "",
          skipAccessTimeAndDistanceCalculations: this.state.skipAccessTimeAndDistanceCalc,
          telehealthAccessCreditPercentage: this.state.creditPercentage,
        });

      servicingCriteria.push({
        geographyId: newCriteria.geographyId,
        servicingAnalysisType: this.state.servicingReqInCounty ? ServicingAnalysisTypeEnum.InRegion : ServicingAnalysisTypeEnum.InRegionOrCoversMember,
        servicingMinimumCount: Number(newCriteria.servicingMinimumCount),
        servicingRatio: newCriteria.servicingRatio,
      });
    })

    this.props.handleSave(
      this.state.accessReqInCounty,
      this.state.categoryDisplayName,
      this.state.creditPercentage,
      this.state.expandable,
      this.state.isProvider,
      this.state.loading,
      this.state.informational,
      this.state.selectedTaxonomyCrosswalk,
      this.state.servicingReqInCounty,
      accessCriteria,
      servicingCriteria,
      this.state.specialtyCode.trim(),
      this.state.specialtyCodeLineItems,
      this.state.specialtyCodeName.trim(),
      this.state.specialtyCodeProviderWeight,
      this.state.specialtyGroupCode.trim(),
      this.state.specialtyGroupCodeName.trim(),
      this.state.taxonomyCode.trim(),
      this.state.taxonomyCrosswalks,
      this.state.taxonomySpecialtyCode.trim(),
      this.state.taxonomySpecialtyCodeLineItems,
      this.state.telehealthCredit,
      this.state.usingRatios,
      this.state.countDistinct,
      Number(this.state.minimumServicingCounts),
      this.state.providerCount,
      this.props.index
    );
    this.props.handleCloseFlyout();
  }

  private validatePanel = () => {
    const servicingDetailsErrorMessages = [];

    if (this.state.specialtyAccessCriteria.find(x => !x.MinimumProviderCountIsValid) ||
      this.state.accessCustomizations.find(x => !x.MinimumProviderCountIsValid)) {
      servicingDetailsErrorMessages.push("Required Providers must be 1 or 2");
    }

    if (this.state.specialtyAccessCriteria.find(x => !x.MaximumDistanceAllowedIsValid && this.state.skipAccessTimeAndDistanceCalc === false) ||
      this.state.accessCustomizations.find(x => !x.MaximumDistanceAllowedIsValid && this.state.skipAccessTimeAndDistanceCalc === false)) {
      servicingDetailsErrorMessages.push("Miles cannot be greater than 9,999 or less than 1");
    }

    if (this.state.specialtyAccessCriteria.find(x => !x.MaximumTimeAllowedIsValid && this.state.skipAccessTimeAndDistanceCalc === false) ||
      this.state.accessCustomizations.find(x => !x.MaximumTimeAllowedIsValid && this.state.skipAccessTimeAndDistanceCalc === false)) {
      servicingDetailsErrorMessages.push("Minutes cannot be greater than 9,999 or less than 1");
    }

    if (this.state.specialtyAccessCriteria.find(x => !x.PassPercentageIsValid) ||
      this.state.accessCustomizations.find(x => !x.PassPercentageIsValid)) {
      servicingDetailsErrorMessages.push("Pass % must be greater than 0 and less than 100");
    }

    if (this.state.usingRatios === false && (
      this.state.specialtyAccessCriteria.find(x => !x.ServicingMinimumCountIsValid) ||
        this.state.accessCustomizations.find(x => !x.ServicingMinimumCountIsValid))) {
      servicingDetailsErrorMessages.push("Servicing Providers must be a number");
    }

    if (this.state.usingRatios && (
      this.state.specialtyAccessCriteria.find(x => !x.ServicingMinimumCountIsValid) ||
        this.state.accessCustomizations.find(x => !x.ServicingMinimumCountIsValid))) {
      servicingDetailsErrorMessages.push("Servicing Providers must be less than Member Count");
    }

    if (this.state.specialtyAccessCriteria.find(x => !x.MemberCountIsValid) ||
      this.state.accessCustomizations.find(x => !x.MemberCountIsValid)) {
      servicingDetailsErrorMessages.push("Member Count must be greater than Servicing Minimum Count");
    }

    if (!this.state.specialtyCodeProviderWeightIsValid || !this.state.specialtyGroupCodeIsValid || !this.state.specialtyGroupCodeNameIsValid
      || this.state.specialtyCodeLineItems.length === 0 || this.state.specialtyGroupCode.length === 0
      || this.state.specialtyCodeLineItems.find(x => x.specialtyCode.length === 0 || x.specialtyCodeName.length === 0)
      || this.state.countDistinct === "unknown" || servicingDetailsErrorMessages.length > 0) {
      this.setState({ panelIsValid: false, servicingDetailsErrorMessages: servicingDetailsErrorMessages });
    } else {
      this.setState({ panelIsValid: true, servicingDetailsErrorMessages: servicingDetailsErrorMessages });
    }
  }

  private createServicingDetailsWafflesTable = async (updatedData: ServicingDetailsModel[] = this.state.specialtyAccessCriteria): Promise<ServicingDetailsRow[]> => {
    const rowData = updatedData.map(rec => {
      /* eslint-disable sort-keys */
      return ({
        key: rec.geographyId,
        geographyId: <>{rec.geographyId}</>,
        requiredProviders:
          (<QESTextInput
            key={rec.geographyId}
            label="Required Providers"
            testId="edit-specialtyGroupServicingDetails__requiredProviders"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2); }}
            onChange={(e) => { this.handleMinimumProviderCountChange(e, rec.geographyId, false) }}
            value={rec.minimumProviderCount}
            isValid={rec.MinimumProviderCountIsValid}
            className={!rec.MinimumProviderCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        miles:
          (<QESTextInput
            label="Miles"
            testId="edit-specialtyGroupServicingDetails__miles"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { this.handleMaximumDistanceAllowedChange(e, rec.geographyId, false); }}
            isValid={rec.MaximumDistanceAllowedIsValid}
            className={!rec.MaximumDistanceAllowedIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            value={rec.maximumDistanceAllowed}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        minutes:
          (<QESTextInput
            label="Minutes"
            testId="edit-specialtyGroupServicingDetails__minutes"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { this.handleMaximumTimeAllowedChange(e, rec.geographyId, false) }}
            isValid={rec.MaximumTimeAllowedIsValid}
            className={!rec.MaximumTimeAllowedIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            value={rec.maximumTimeAllowed}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        passPercentage:
          (<QESTextInput
            label="Pass %"
            testId="edit-specialtyGroupServicingDetails__passPercentage"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100); }}
            onChange={(e) => { this.handlePassPercentageChange(e, rec.geographyId, false) }}
            isValid={rec.PassPercentageIsValid}
            className={!rec.PassPercentageIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            value={rec.passPercentage}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        servicingProviders:
          <div className="flex-container">
            {this.state.usingRatios === false && (
              <QESTextInput
                label="Servicing Providers"
                testId="edit-specialtyGroupServicingDetails__servicingProviders"
                containerClassName={this.state.usingRatios ? "d-inline-block w-50 pr-1" : ""}
                onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsNoLimit); }}
                onChange={(e) => { this.handleServicingMinimumCountChange(e, rec.geographyId, false) }}
                isValid={this.state.specialtyAccessCriteria.find(x => x.geographyId === rec.geographyId)?.ServicingMinimumCountIsValid}
                className={!rec.ServicingMinimumCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
                value={rec.servicingMinimumCount}
                hideLabel={true}
                unrequiredValidation={true}
              />
            )}
            {this.state.usingRatios === true && (
              <>
                <QESTextInput
                  label="Servicing Providers"
                  testId="edit-specialtyGroupServicingDetails__servicingProviders"
                  containerClassName={this.state.usingRatios ? "d-inline-block w-50 pr-1" : ""}
                  className={!rec.ServicingMinimumCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
                  isValid={rec.ServicingMinimumCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                  onChange={(e) => { this.handleServicingMinimumCountChange(e, rec.geographyId, false) }}
                  value={rec.servicingMinimumCount}
                  hideLabel={true}
                  unrequiredValidation={true}
                />
                <QESTextInput
                  label="Members"
                  testId="edit-specialtyGroupServicingDetails__members"
                  containerClassName="d-inline-block w-50 pl-1"
                  className={!rec.MemberCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
                  isValid={rec.MemberCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                  onChange={(e) => { this.handleMembersCountChange(e, rec.geographyId, false); }}
                  value={rec.memberCounts}
                  hideLabel={true}
                  unrequiredValidation={true}
                />
              </>
            )}
          </div>,
        calculationMethod:
          (<QESSelect
            testId="create-manage-specialtyGroup-panel-content__urbanCalculationMethod"
            containerClassName="mb-1 ml-4"
            label=""
            onChange={(e) => {
              this.handleCalculationMethodChange(e.target.value as CalculationMethodEnum, rec.geographyId, false);
            }}
            defaultValue={rec.calculationMethod}
          >
            {calculationMethodDropdownValues.map((calculationMethod, index) => <option key={index} value={calculationMethod.value}>{calculationMethod.displayText}</option>)}
          </QESSelect>),
      });
    });

    this.validatePanel();
    this.setState({ servicingDetailsData: rowData });
    return rowData;
  }

  private createCustomizationsWafflesTable = async (updatedData: ServicingDetailsModel[] = this.state.accessCustomizations): Promise<ServicingDetailsRow[]> => {
    const rowData = updatedData.map(rec => {
      /* eslint-disable sort-keys */
      return ({
        key: rec.geographyId,
        geographyId: (<QESSelect
          testId="edit-accessCustomizations__geographyId"
          containerClassName="mb-1 ml-4"
          label=""
          value={rec.geographyId}
          isValid={rec.CountyIsValid}
          onChange={(e) => {
            this.handleCountyChange(e, rec.geographyId);
          }}
          defaultValue={rec.geographyId}
        >
          {this.state.counties.map((county, index) => <option key={index} value={county}>{county}</option>)}
        </QESSelect>),
        requiredProviders:
          (<QESTextInput
            key={rec.geographyId}
            label="Required Providers"
            testId="edit-accessCustomizations__requiredProviders"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2); }}
            onChange={(e) => { this.handleMinimumProviderCountChange(e, rec.geographyId, true) }}
            value={rec.minimumProviderCount}
            isValid={rec.MinimumProviderCountIsValid}
            className={!rec.MinimumProviderCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        miles:
          (<QESTextInput
            label="Miles"
            testId="edit-accessCustomizations__miles"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { this.handleMaximumDistanceAllowedChange(e, rec.geographyId, true); }}
            isValid={rec.MaximumDistanceAllowedIsValid}
            className={!rec.MaximumDistanceAllowedIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            value={rec.maximumDistanceAllowed}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        minutes:
          (<QESTextInput
            label="Minutes"
            testId="edit-accessCustomizations__minutes"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { this.handleMaximumTimeAllowedChange(e, rec.geographyId, true) }}
            isValid={rec.MaximumTimeAllowedIsValid}
            className={!rec.MaximumTimeAllowedIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            value={rec.maximumTimeAllowed}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        passPercentage:
          (<QESTextInput
            label="Pass %"
            testId="edit-accessCustomizations__passPercentage"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100); }}
            onChange={(e) => { this.handlePassPercentageChange(e, rec.geographyId, true) }}
            isValid={rec.PassPercentageIsValid}
            className={!rec.PassPercentageIsValid && this.state.highlightErrors ? "border border-danger" : ""}
            value={rec.passPercentage}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        servicingProviders:
          <div className="flex-container">
            {this.state.usingRatios === false && (
              <QESTextInput
                label="Servicing Providers"
                testId="edit-accessCustomizations__servicingProviders"
                containerClassName={this.state.usingRatios ? "d-inline-block w-50 pr-1" : ""}
                onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsNoLimit); }}
                onChange={(e) => { this.handleServicingMinimumCountChange(e, rec.geographyId, true) }}
                isValid={this.state.specialtyAccessCriteria.find(x => x.geographyId === rec.geographyId)?.ServicingMinimumCountIsValid ?? true}
                className={!rec.ServicingMinimumCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
                value={rec.servicingMinimumCount}
                hideLabel={true}
                unrequiredValidation={true}
                disabled={!rec.geographyId}
              />
            )}
            {this.state.usingRatios === true && (
              <>
                <QESTextInput
                  label="Servicing Providers"
                  testId="edit-accessCustomizations__servicingProviders"
                  containerClassName={this.state.usingRatios ? "d-inline-block w-50 pr-1" : ""}
                  className={!rec.ServicingMinimumCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
                  isValid={rec.ServicingMinimumCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                  onChange={(e) => { this.handleServicingMinimumCountChange(e, rec.geographyId, true) }}
                  value={rec.servicingMinimumCount}
                  hideLabel={true}
                  unrequiredValidation={true}
                  disabled={!rec.geographyId}
                />
                <QESTextInput
                  label="Members"
                  testId="edit-accessCustomizations__members"
                  containerClassName="d-inline-block w-50 pl-1"
                  className={!rec.MemberCountIsValid && this.state.highlightErrors ? "border border-danger" : ""}
                  isValid={rec.MemberCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                  onChange={(e) => { this.handleMembersCountChange(e, rec.geographyId, true); }}
                  value={rec.memberCounts}
                  hideLabel={true}
                  unrequiredValidation={true}
                  disabled={!rec.geographyId}
                />
              </>
            )}
          </div>,
        calculationMethod:
          <QESSelect
            testId="edit-accessCustomizations__urbanCalculationMethod"
            containerClassName="mb-1 ml-4"
            label=""
            onChange={(e) => {
              this.handleCalculationMethodChange(e.target.value as CalculationMethodEnum, rec.geographyId, true);
            }}
            defaultValue={rec.calculationMethod}
            disabled={!rec.geographyId}
            value={rec.calculationMethod}
          >
            {calculationMethodDropdownValues.map((calculationMethod, index) => <option key={index} value={calculationMethod.value}>{calculationMethod.displayText}</option>)}
          </QESSelect>,
        remove:
          <QESButton
              qesTestId="edit-accessCustomizations__remove"
              className="mt10"
              qesType="secondary-danger"
              onClick={() => this.handleRemoveAccessCustomization(rec.geographyId)}>
            Remove
          </QESButton>
      });
    });

    this.validatePanel();
    this.setState({ accessCustomizationsData: rowData });
    return rowData;
  }

  public render = () => {

    const classificationColumnConfig: ColumnConfig = {
      geographyId: {
        displayText: "Standard",
        sizeContentToFit: true,
        testId: "specialtyGroup-geographyId"
      },
      requiredProviders: {
        displayText: "Required Providers",
        sizeContentToFit: true,
        testId: "specialtyGroup-requiredProviders",
        tooltip: "Access Providers must be 1 or 2",
        displayElement: <>Required Providers <span className="text-danger">*</span></>
      },
      miles: {
        displayText: "Miles",
        sizeContentToFit: true,
        testId: "specialtyGroup-miles",
        tooltip: "Miles cannot be greater than 9,999",
      },
      minutes: {
        displayText: "Minutes",
        sizeContentToFit: true,
        testId: "specialtyGroup-minutes",
        tooltip: "Minutes cannot be greater than 9,999",
      },
      passPercentage: {
        displayText: "Pass %",
        sizeContentToFit: true,
        testId: "specialtyGroup-passPercentage",
        tooltip: "Pass percentage cannot be greater than 100",
        displayElement: <>Pass % {(!this.state.accessReqInCounty && !this.state.skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
      },
      servicingProviders: {
        displayText: "Servicing Providers",
        sizeContentToFit: true,
        testId: "specialtyGroup-servicingProviders",
        tooltip: this.state.usingRatios ? "These fields should be populated as Provider to Member Ratios. Ex. 1:1000" : "Access Providers cannot be greater than Servicing Providers",
      },
      calculationMethod: {
        displayText: "Calculation Method",
        sizeContentToFit: true,
        testId: "specialtyGroup-calculationMethod",
      }
    };

    const classificationSkipAccessCheckedColumnConfig: ColumnConfig = {
      geographyId: {
        displayText: "Standard",
        sizeContentToFit: true,
        testId: "specialtyGroup-geographyId"
      },
      requiredProviders: {
        displayText: "Required Providers",
        sizeContentToFit: true,
        testId: "specialtyGroup-requiredProviders",
        displayElement: <>Required Providers <span className="text-danger">*</span></>
      },
      passPercentage: {
        displayText: "Pass %",
        sizeContentToFit: true,
        testId: "specialtyGroup-passPercentage",
        displayElement: <>Pass % {(!this.state.accessReqInCounty && !this.state.skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
      },
      servicingProviders: {
        displayText: "Servicing Providers",
        sizeContentToFit: true,
        testId: "specialtyGroup-servicingProviders",
      },
      calculationMethod: {
        displayText: "Calculation Method",
        sizeContentToFit: true,
        testId: "specialtyGroup-calculationMethod",
      }
    };

    const customizationColumnConfig: ColumnConfig = {
      geographyId: {
        displayText: "County SSA",
        sizeContentToFit: true,
        testId: "accessCustomization-geographyId"
      },
      requiredProviders: {
        displayText: "Required Providers",
        sizeContentToFit: true,
        testId: "accessCustomization-requiredProviders",
        tooltip: "Access Providers must be 1 or 2",
        displayElement: <>Required Providers <span className="text-danger">*</span></>
      },
      miles: {
        displayText: "Miles",
        sizeContentToFit: true,
        testId: "accessCustomization-miles",
        tooltip: "Miles cannot be greater than 9,999",
      },
      minutes: {
        displayText: "Minutes",
        sizeContentToFit: true,
        testId: "accessCustomization-minutes",
        tooltip: "Minutes cannot be greater than 9,999",
      },
      passPercentage: {
        displayText: "Pass %",
        sizeContentToFit: true,
        testId: "accessCustomization-passPercentage",
        tooltip: "Pass percentage cannot be greater than 100",
        displayElement: <>Pass % {(!this.state.accessReqInCounty && !this.state.skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
      },
      servicingProviders: {
        displayText: "Servicing Providers",
        sizeContentToFit: true,
        testId: "accessCustomization-servicingProviders",
        tooltip: this.state.usingRatios ? "These fields should be populated as Provider to Member Ratios. Ex. 1:1000" : "Access Providers cannot be greater than Servicing Providers",
      },
      calculationMethod: {
        displayText: "Calculation Method",
        sizeContentToFit: true,
        testId: "accessCustomization-calculationMethod",
      },
      remove: {
        displayText: "",
        sizeContentToFit: true,
        testId: "accessCustomiztion-remove"
      }
    };

    const customizationSkipAccessCheckedColumnConfig: ColumnConfig = {
      geographyId: {
        displayText: "County SSA",
        sizeContentToFit: true,
        testId: "accessCustomization-geographyId"
      },
      requiredProviders: {
        displayText: "Required Providers",
        sizeContentToFit: true,
        testId: "accessCustomization-requiredProviders",
        displayElement: <>Required Providers <span className="text-danger">*</span></>
      },
      passPercentage: {
        displayText: "Pass %",
        sizeContentToFit: true,
        testId: "accessCustomization-passPercentage",
        displayElement: <>Pass % {(!this.state.accessReqInCounty && !this.state.skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
      },
      servicingProviders: {
        displayText: "Servicing Providers",
        sizeContentToFit: true,
        testId: "accessCustomization-servicingProviders",
      },
      calculationMethod: {
        displayText: "Calculation Method",
        sizeContentToFit: true,
        testId: "accessCustomization-calculationMethod",
      },
      remove: {
        displayText: "",
        sizeContentToFit: true,
        testId:"access-Customization-remove"
      }
    };

    const namedCategories = this.props.templateDetails?.categories?.filter(function (category) {
      return category.displayName != "" && category.displayName != undefined && category.displayName != null;
    });

    if (namedCategories?.length == 1) {
      this.setState({ categoryDisplayName: namedCategories[0].displayName })
    }

    const setCreditPercentage = (telehealthCredit: boolean) => {
      telehealthCredit ? this.setState({ creditPercentage: 10 }) : this.setState({ creditPercentage: 0 });
    }

    const handleAdd = () => {
      const specialtyCodeLineItems: SpecialtyCodeLineItem[] = cloneDeep(this.state.specialtyCodeLineItems);

      const newItem = {
        id: this.state.specialtyCodeLineItems.length,
        specialtyCode: this.state.specialtyCode.trim(),
        specialtyCodeName: this.state.specialtyCodeName.trim(),
        specialtyCodeProviderWeight: this.state.specialtyCodeProviderWeight,
      } as SpecialtyCodeLineItem;

      let isDuplicate = false;

      specialtyCodeLineItems.forEach(lineItem => {
        if(newItem.specialtyCode === lineItem.specialtyCode
            || newItem.specialtyCodeName === lineItem.specialtyCodeName ) {
          isDuplicate = true;
        }
      });
      if(!isDuplicate) {
        this.setState({ duplicateSpecialtyCodeLineItem: false });
        specialtyCodeLineItems.push(newItem);
        this.setState({ specialtyCode: "", specialtyCodeName: "", specialtyCodeProviderWeight: "1", specialtyCodeLineItems: specialtyCodeLineItems });
      }
      else {
        this.setState({ duplicateSpecialtyCodeLineItem: true });
      }
    }

    const handleRemove = (lineItem: SpecialtyCodeLineItem) => {
      let specialtyCodeLineItems: SpecialtyCodeLineItem[] = cloneDeep(this.state.specialtyCodeLineItems);
      specialtyCodeLineItems = specialtyCodeLineItems.filter(function (item) {
        return item.id !== lineItem.id;
      });
      this.setState({ specialtyCodeLineItems: specialtyCodeLineItems, duplicateSpecialtyCodeLineItem: false });
    }

    const handleTaxonomySpecialtyCodeAdd = () => {
      const taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[] = cloneDeep(this.state.taxonomySpecialtyCodeLineItems);

      const newItem = {
        id: this.state.taxonomySpecialtyCodeLineItems?.length,
        taxonomyCode: this.state.taxonomyCode,
        taxonomySpecialtyCode: this.state.taxonomySpecialtyCode,
      } as TaxonomySpecialtyCodeLineItem;
      taxonomySpecialtyCodeLineItems.push(newItem);
      this.setState({ taxonomyCode: "", taxonomySpecialtyCode: "", taxonomySpecialtyCodeLineItems: taxonomySpecialtyCodeLineItems });
    }

    const handleTaxonomySpecialtyCodeRemove = (lineItem: TaxonomySpecialtyCodeLineItem) => {
      let taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[] = cloneDeep(this.state.taxonomySpecialtyCodeLineItems);
      taxonomySpecialtyCodeLineItems = taxonomySpecialtyCodeLineItems.filter(function (item) {
        return item.id !== lineItem.id;
      });
      this.setState({ taxonomySpecialtyCodeLineItems: taxonomySpecialtyCodeLineItems });
    }

    const handleSkipAccessCheckboxToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ accessReqInCounty: e.target.checked });
      let updatedData = this.state.specialtyAccessCriteria;
      if (e.target.checked) {
        updatedData = this.state.specialtyAccessCriteria.map(row => ({ ...row, PassPercentageIsValid: true }));
      }
      else {
        updatedData = this.state.specialtyAccessCriteria.map(row => ({ ...row, PassPercentageIsValid: false }));
      }
      this.setState({ specialtyAccessCriteria: updatedData, skipAccessTimeAndDistanceCalc: e.target.checked });
      await this.createServicingDetailsWafflesTable(updatedData);
      this.validatePanel();
    }

    const handleInCountyRequirementsCheckboxToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
      let updatedData = this.state.specialtyAccessCriteria;
      if (e.target.checked) {
        updatedData = this.state.specialtyAccessCriteria.map(row => ({ ...row, maximumDistanceAllowed: 0, maximumTimeAllowed: 0, PassPercentageIsValid: true }));
      }
      else if (!this.state.skipAccessTimeAndDistanceCalc) {
        updatedData = this.state.specialtyAccessCriteria.map(row => ({ ...row, PassPercentageIsValid: false }));
      }
      this.setState({ specialtyAccessCriteria: updatedData, accessReqInCounty: e.target.checked }, () => this.createServicingDetailsWafflesTable(updatedData));
      this.validatePanel();
    }

    const handleProviderCountChange = async (value: string) => {
      let newValue: string = value;
      if (isNaN(parseInt(newValue, 10))) { //default the value to Zero if it is an invalid number
        newValue = "0";
      }
      const newProviderCountValue: number = parseInt(newValue, 10);
      let updatedData = this.state.specialtyAccessCriteria;
      let isValid = true;
      if (!this.execRegex(newProviderCountValue.toString(), new RegExp(InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100))) {
        isValid = false;
      }
      this.setState({ providerCount: newProviderCountValue });
      updatedData = this.state.specialtyAccessCriteria.map(row => ({ ...row, minimumProviderCount: newProviderCountValue, MinimumProviderCountIsValid: isValid }));
      this.setState({ specialtyAccessCriteria: updatedData }, () => { this.createServicingDetailsWafflesTable(); this.createCustomizationsWafflesTable(); });
    }

    const handleMinimumServicingCountsChange = async (value: string) => {
      let newValue: string = value;
      if (isNaN(parseFloat(newValue))) { //default the value to Zero if it is an invalid number
        newValue = "0";
      }
      const newservicingMinimumCountsValue: number = parseFloat(newValue);
      let updatedData = this.state.specialtyAccessCriteria;
      updatedData = this.state.specialtyAccessCriteria.map(row => ({
        ...row,
        servicingMinimumCount: newservicingMinimumCountsValue,
        servicingRatio: this.state.usingRatios === true ? newservicingMinimumCountsValue + ":" + row.memberCounts : "0:0",
        ServicingMinimumCountIsValid: this.state.usingRatios ? +newservicingMinimumCountsValue <= +row.memberCounts : true,
        MemberCountIsValid: this.state.usingRatios ? +newservicingMinimumCountsValue <= +row.memberCounts : true
      }));
      this.setState({ specialtyAccessCriteria: updatedData, minimumServicingCounts: newservicingMinimumCountsValue }, () => { this.createServicingDetailsWafflesTable(); this.createCustomizationsWafflesTable(); });
    }

    const handleMemberCountsChange = async (value: string) => {
      let newValue: string = value;
      if (isNaN(parseFloat(newValue))) { //default the value to Zero if it is an invalid number
        newValue = "0";
      }
      const newMemberCounts: number = parseFloat(newValue);
      let updatedData = this.state.specialtyAccessCriteria;
      updatedData = this.state.specialtyAccessCriteria.map(row => ({
        ...row,
        memberCounts: newMemberCounts,
        servicingRatio: row.servicingMinimumCount + ":" + newMemberCounts,
        ServicingMinimumCountIsValid: this.state.usingRatios ? +row.servicingMinimumCount <= +newMemberCounts : true,
        MemberCountIsValid: this.state.usingRatios ? +row.servicingMinimumCount <= +newMemberCounts : true
      }));
      this.setState({ specialtyAccessCriteria: updatedData, memberCounts: newMemberCounts }, () => { this.createServicingDetailsWafflesTable(); this.createCustomizationsWafflesTable(); });
    }

    const handleUsingRatiosCheckboxToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ usingRatios: e.target.checked }, () => { this.createServicingDetailsWafflesTable(); this.createCustomizationsWafflesTable(); });
    }

    const getErrorMessages = (): JSX.Element => {
      return <>{this.state.servicingDetailsErrorMessages.map((errorMessage) => (
        <QESAlert type={"danger"} key={errorMessage} persist={true}>
          <span>{errorMessage}</span>
        </QESAlert>
      ))}
      </>
    }


    return (
      <>
        {this.props.loading || this.state.loading ? <LoadingSpinner /> :
          <>
            <div className="d-flex flex-row">
              <div className="w-25 p-2 bd-highlight">
                <QESTextInput
                  testId={"create-manage-specialtyGroup-panel-content__specialtyGroupCode"}
                  containerClassName="mb-2"
                  label="Specialty Group Code"
                  value={this.state.specialtyGroupCode}
                  onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                  onChange={(e) => {
                    this.handleSpecialtyGroupCodeChange(e.target.value);
                    this.validateName();
                  }}
                  isValid={this.state.specialtyGroupCodeIsValid && this.state.specialtyGroupCode.length != 0}
                  required={true}
                  descriptiveText={!(this.state.specialtyGroupCodeIsValid) && "A specialty group with this code already exists."}
                  errorText={this.state.nameIsValidText}
                  className={this.state.specialtyGroupCode.length === 0 && this.state.highlightErrors ? "border border-danger" : ""}
                  wasFocusedExternally={!!this.props.specialtyGroup}
                />
              </div>
              <div className="w-25 p-2 bd-highlight">
                <QESTextInput
                  testId={"create-manage-specialtyGroup-panel-content__specialtyGroupCodeName"}
                  containerClassName="mb-2"
                  label="Specialty Group Name"
                  value={this.state.specialtyGroupCodeName}
                  onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                  onChange={(e) => this.handleSpecialtyGroupNameChange(e.target.value) }
                  isValid={this.state.specialtyGroupCodeNameIsValid && this.state.specialtyGroupCodeName.length != 0}
                  required={true}
                  descriptiveText={!(this.state.specialtyGroupCodeNameIsValid) && "A specialty group with this name already exists."}
                  errorText={this.state.nameIsValidText}
                  className={this.state.specialtyGroupCodeName.length === 0 && this.state.highlightErrors ? "border border-danger" : ""}
                  unrequiredValidation={true}
                  wasFocusedExternally={!!this.props.specialtyGroup}
                />
              </div>
            </div>

            <div className="d-flex flex-row">
              <div className="w-25 p-2 bd-highlight">
                <QESTextInput
                  testId={"create-specialty-panel-content__specialtyCodeInput"}
                  containerClassName="mb-2"
                  label="Specialty Code"
                  value={this.state.specialtyCode}
                  onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                  onChange={(e) => {
                    this.setState({ specialtyCode: e.target.value });
                    this.validateName();
                  }}
                  isValid={this.state.specialtyCode.length != 0}
                  required={this.state.specialtyCodeLineItems.length === 0}
                  errorText={this.state.nameIsValidText}
                  className={this.state.specialtyCode.length === 0 && this.state.specialtyCodeLineItems.length === 0 && this.state.highlightErrors ? "border border-danger" : ""}
                  wasFocusedExternally={!!this.props.specialtyGroup}
                />
              </div>
              <div className="w-25 p-2 bd-highlight">
                <QESTextInput
                  testId={"create-specialty-panel-content__specialtyCodeNameInput"}
                  containerClassName="mb-2"
                  label="Specialty Name"
                  value={this.state.specialtyCodeName}
                  onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                  onChange={(e) => {
                    this.setState({ specialtyCodeName: e.target.value });
                    this.validateName();
                  }}
                  isValid={this.state.specialtyCodeName.length != 0}
                  required={this.state.specialtyCodeLineItems.length === 0}
                  errorText={this.state.nameIsValidText}
                  className={this.state.specialtyCodeName.length === 0 && this.state.specialtyCodeLineItems.length === 0 && this.state.highlightErrors ? "border border-danger" : ""}
                  wasFocusedExternally={!!this.props.specialtyGroup}
                />
              </div>
              <div className="p-2 bd-highlight">
                <QESTextInput
                  testId={"create-specialty-panel-content__specialtyCodeProviderWeightInput"}
                  containerClassName="mb-2 w-75"
                  label="Provider Weight"
                  value={this.state.specialtyCodeProviderWeight}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsLessThanEqual1AndAllowDecimalPlace, true); }}
                  onChange={(e) => { this.handleSpecialtyCodeProviderWeightChange(e) }}
                  isValid={this.state.specialtyCodeProviderWeightIsValid}
                  required={this.state.specialtyCodeLineItems.length === 0}
                  errorText={"Provider Weight cannot be greater than 1"}
                  className={(!this.state.specialtyCodeProviderWeightIsValid || +this.state.specialtyCodeProviderWeight > 1) && this.state.highlightErrors ? "border border-danger" : ""}
                  unrequiredValidation={true}
                  wasFocusedExternally={!!this.props.specialtyGroup}
                />
              </div>
              <div className="pt-3 align-content-center bd-highlight">
                <QESButton
                  qesTestId={`add-specialty__specialty-code__add-button`}
                  qesType="secondary"
                  onClick={handleAdd}
                  disabled={this.state.specialtyCodeName.length === 0 || this.state.specialtyCode.length === 0}
                  className={this.state.specialtyCodeLineItems.length === 0 && this.state.highlightErrors ? "border border-danger" : ""}
                >
                  Add
                </QESButton>
                {this.state.duplicateSpecialtyCodeLineItem && (
                  <span style={{color:"#cc3a52", fontWeight:"bold"}}>
                    <i className="fa fa-times-circle" aria-hidden="true"/> Cannot add a specialty with a duplicate code or name.
                  </span>
                )}
              </div>
            </div>

            {this.state.specialtyCodeLineItems?.length > 0 &&
              <React.Fragment>
                {
                  this.state.specialtyCodeLineItems.map((lineItem) => (
                    <div className="d-flex flex-row" key={lineItem.id}>
                      <div className="w-25 p-2 bd-highlight">
                        <QESTextInput
                          testId={"create-specialty-panel-content__specialtyCodeLineItem"}
                          containerClassName="mb-2"
                          disabled={true}
                          label=""
                          value={lineItem.specialtyCode}
                          onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                          onChange={(e) => {
                            this.setState({ specialtyCode: e.target.value });
                            this.validateName();
                          }}
                        />
                      </div>
                      <div className="w-25 p-2 bd-highlight">
                        <QESTextInput
                          testId={"create-specialty-panel-content__specialtyCodeNameLineItem"}
                          containerClassName="mb-2"
                          disabled={true}
                          label=""
                          value={lineItem.specialtyCodeName}
                          onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                          onChange={(e) => {
                            this.setState({ specialtyCodeName: e.target.value });
                            this.validateName();
                          }}
                        />
                      </div>
                      <div className="p-2 bd-highlight">
                        <QESTextInput
                          testId={"create-specialty-panel-content__specialtyCodeProviderWeightLineItem"}
                          containerClassName="mb-2"
                          disabled={true}
                          label=""
                          value={lineItem.specialtyCodeProviderWeight}
                          onChange={(e) => {
                            this.setState({ specialtyCodeProviderWeight: e.target.value });
                            this.validateName();
                          }}
                        />
                      </div>
                      <div className="pt-3 bd-highlight">
                        <QESButton qesTestId={`add-specialty__specialty-code__remove-button`} qesType="secondary-danger" onClick={() => handleRemove(lineItem)}>
                          Remove
                        </QESButton>
                      </div>
                    </div>
                  ))
                }
              </React.Fragment>
            }
            {this.props.features.has(Features.EDIT_TAXONOMY_CODE_MAPS) &&
              <React.Fragment>
                <QESSelect
                  testId="create-manage-specialtyGroup-panel-content__taxonomyCrosswalks"
                  containerClassName="p-2 w-25 bd-highlight"
                  className="mb-2"
                  label="Taxonomy Crosswalks"
                  value={this.state.selectedTaxonomyCrosswalk}
                  isValid={!!this.state.selectedTaxonomyCrosswalk.toString()}
                  disabled={!this.props.allowClientEdit}
                  onChange={(e) => {
                    this.setState({ selectedTaxonomyCrosswalk: e.target.value });
                    this.handleTaxonomyCrosswalkChange(e.target.value);
                    this.validateName();
                  }}
                >
                  {Object.values(this.state.taxonomyCrosswalks).map((x, index) => <option key={index} value={x.key}>{x.props.children}</option>)}
                </QESSelect>
                <div className="d-flex flex-row">
                  <div className="w-25 p-2 bd-highlight">
                    <QESTextInput
                      testId={"create-specialty-panel-content__taxonomyCode"}
                      containerClassName="mb-2"
                      label="Taxonomy Code"
                      value={this.state.taxonomyCode}
                      onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                      onChange={(e) => {
                        this.setState({ taxonomyCode: e.target.value });
                        this.validateName();
                      }}
                      disabled={this.state.selectedTaxonomyCrosswalk === '' ? true : false}
                      isValid={this.state.nameIsValid}
                      errorText={this.state.nameIsValidText}
                      wasFocusedExternally={!!this.props.specialtyGroup}
                    />
                  </div>
                  <div className="w-25 p-2 bd-highlight">
                    <QESTextInput
                      testId={"create-specialty-panel-content__specialtyCode"}
                      containerClassName="mb-2"
                      label="Specialty Code"
                      value={this.state.taxonomySpecialtyCode}
                      onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                      onChange={(e) => {
                        this.setState({ taxonomySpecialtyCode: e.target.value });
                        this.validateName();
                      }}
                      disabled={this.state.selectedTaxonomyCrosswalk === '' ? true : false}
                      isValid={this.state.nameIsValid}
                      errorText={this.state.nameIsValidText}
                      wasFocusedExternally={!!this.props.specialtyGroup}
                    />
                  </div>
                  <div className="pt-3 align-content-center bd-highlight">
                    <QESButton qesTestId={`add-specialty__taxonomySpecialty-code__add-button`} qesType="secondary" onClick={handleTaxonomySpecialtyCodeAdd}>
                      Add
                    </QESButton>
                  </div>
                </div>
              </React.Fragment>
            }
            {this.state.taxonomySpecialtyCodeLineItems?.length > 0 &&
              <React.Fragment>
                {
                  this.state.taxonomySpecialtyCodeLineItems.map((lineItem) => (
                    <div className="d-flex flex-row" key={lineItem.id}>
                      <div className="w-25 p-2 bd-highlight">
                        <QESTextInput
                          testId={"create-specialty-panel-content__taxonomyCodeLineItem"}
                          containerClassName="mb-2"
                          label=""
                          value={lineItem.taxonomyCode}
                          onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                          onChange={(e) => {
                            this.setState({ taxonomyCode: e.target.value });
                            this.validateName();
                          }}
                        />
                      </div>
                      <div className="w-25 p-2 bd-highlight">
                        <QESTextInput
                          testId={"create-specialty-panel-content__taxonomySpecialtyCodeLineItem"}
                          containerClassName="mb-2"
                          label=""
                          value={lineItem.taxonomySpecialtyCode}
                          onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                          onChange={(e) => {
                            this.setState({ taxonomySpecialtyCode: e.target.value });
                            this.validateName();
                          }}
                        />
                      </div>
                      <div className="pt-3 bd-highlight">
                        <QESButton qesTestId={`add-specialty__taxonomySpecialty-code__remove-button`} qesType="secondary-danger" onClick={() => handleTaxonomySpecialtyCodeRemove(lineItem)}>
                          Remove
                        </QESButton>
                      </div>
                    </div>
                  ))
                }
              </React.Fragment>}

            {namedCategories?.length > 1 &&
              <div className="p-1 bd-highlight">
                <QESSelect
                  testId="create-manage-specialtyGroup-panel-content__category"
                  containerClassName="p-2 w-25 bd-highlight"
                  className="mb-2"
                  label="Category"
                  value={this.state.categoryDisplayName}
                  onChange={(e) => {
                    this.handleCategoryChange(e.target.value);
                  }}
                >
                  {Object.values(namedCategories).map((x, index) => <option key={index} value={x.displayName}>{x.displayName}</option>)}
                </QESSelect>
              </div>
            }

            <div className="d-flex flex-row my-4">
              <div className="w-25 p-2 bd-highlight">
                <p className="qes-text-input__label mb-2">Specialty Details</p>
                <QESCheckbox
                  containerClassName="mb-3"
                  testId={"create-manage-specialtyGroup-panel-content__expandable"}
                  label="Expandable"
                  checked={this.state.expandable}
                  onChange={(e) => this.setState({ expandable: e.target.checked })}
                />
                <QESCheckbox
                  containerClassName="mb-3"
                  testId={"create-manage-specialtyGroup-panel-content__Informational"}
                  label="Informational"
                  checked={this.state.informational}
                  onChange={(e) => this.setState({ informational: e.target.checked })}
                />
                <QESCheckbox
                  containerClassName="mb-3"
                  testId={"create-manage-specialtyGroup-panel-content__usingRatios"}
                  label="Using Ratios"
                  checked={this.state.usingRatios}
                  onChange={(e) => { handleUsingRatiosCheckboxToggle(e); }}
                />
                <QESCheckbox
                  containerClassName="mb-3"
                  testId={"create-manage-specialtyGroup-panel-content__telehealthCredit"}
                  label="Telehealth Credit"
                  checked={this.state.telehealthCredit}
                  onChange={(e) => {
                    this.setState({ telehealthCredit: e.target.checked })
                    setCreditPercentage(e.target.checked);
                  }}
                />

                <QESTextInput
                  testId={"create-specialty-panel-content__creditPercentage"}
                  className="col-4"
                  containerClassName="mb-2 ml-4"
                  label="Credit Percentage"
                  value={this.state.creditPercentage}
                  disabled={!this.state.telehealthCredit}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100); }}
                  onChange={(e) => { this.handleCreditPercentageChange(e) }}
                  isValid={this.state.creditPercentageIsValid}
                  errorText={"Telehealth credit Percentage cannot be higher than 100"}
                  unrequiredValidation={true}
                />
              </div>
            </div>

            <div className="ml-2 mb-5">
              <p className="qes-text-input__label">Provider or Facility Option?</p>
              <QESRadioButton
                label="Provider"
                value="provider"
                radioContainerClassName="mb-3"
                checked={this.state.isProvider}
                onChange={() => { this.setState({ isProvider: true }); }}
                testId={`create-specialty-panel-content__isProviderRadio`}
              />
              <QESRadioButton
                label="Facility"
                value="facility"
                checked={!this.state.isProvider}
                onChange={() => { this.setState({ isProvider: false }); }}
                testId={`create-specialty-panel-content__isFacilityRadio`}
              />
            </div>

            <div className="ml-2 mb-5">
              <p className="qes-text-input__label">Access Requirements</p>
              <QESCheckbox
                containerClassName="mb-3"
                testId={"create-manage-specialtyGroup-panel-content__skipAccessTimeAndDistanceCalc"}
                label="Skip Access Time and Distance"
                checked={this.state.skipAccessTimeAndDistanceCalc}
                onChange={(e) => handleSkipAccessCheckboxToggle(e)}
              />
              <QESCheckbox
                containerClassName="mb-3"
                testId={"create-manage-specialtyGroup-panel-content__accessRequirements"}
                label="In-County Requirements"
                checked={this.state.accessReqInCounty}
                onChange={(e) => handleInCountyRequirementsCheckboxToggle(e)}
              />
            </div>
            <div className="ml-2 mb-5">
              <p className="qes-text-input__label">Servicing Requirements</p>
              <QESCheckbox
                containerClassName="mb-4"
                testId={"create-manage-specialtyGroup-panel-content__servicingRequirements"}
                label="In-County Requirements"
                checked={this.state.servicingReqInCounty}
                onChange={(e) => this.setState({ servicingReqInCounty: e.target.checked })}
              />
            </div>
            <div className="ml-2 mr-4 p-2 border">
              <div className="header-s pt-2">
                Servicing Details
              </div>
              {/* RatiosAndCountsTable */}
              <div className="d-flex flex-row my-4">
                <div className="p-1 bd-highlight">
                  <QESTextInput
                    testId={"create-specialty-panel-content__providerCount"}
                    containerClassName="mb-1 label-s"
                    label={
                      <TooltipWrapper text={"This field auto populates the Required Providers column in the table below"} position="top">
                        <i className={"fa fa-info-circle waffles-tooltip-indicator"} /> Provider Count
                      </TooltipWrapper>
                    }
                    value={this.state.providerCount ? this.state.providerCount : 0}
                    onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2); }}
                    onChange={(e) => { handleProviderCountChange(e.target.value); }}
                  />
                </div>
                <div className={"p-1 bd-highlight" + (this.state.usingRatios ? " col-3" : "")}>
                  <div className="d-flex flex-column">
                    <label className="qes-text-input__label" htmlFor="create-specialty-panel-content__minimumServicingCounts">
                      <TooltipWrapper text={"This field auto populates the Servicing Providers column in the table below"} position="top">
                        <i className={"fa fa-info-circle waffles-tooltip-indicator"} /> Minimum Servicing Counts
                      </TooltipWrapper>
                    </label>
                    <div className="d-flex flex-row">
                      {this.state.usingRatios === false &&
                        <QESTextInput
                          testId={"create-specialty-panel-content__minimumServicingCounts"}
                          containerClassName={"mb-1 mr-1 label-s"}
                          label=""
                          hideLabel={true}
                          value={this.state.minimumServicingCounts ? this.state.minimumServicingCounts : 0}
                          onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsNoLimit); }}
                          onChange={(e) => {
                            handleMinimumServicingCountsChange(e.target.value);
                          }}
                        />
                      }
                      {this.state.usingRatios === true &&
                        <>
                          <QESTextInput
                            testId={"create-specialty-panel-content__minimumServicingCounts"}
                            containerClassName={"mb-1 mr-1 label-s"}
                            className={this.state.usingRatios && +this.state.minimumServicingCounts > +this.state.memberCounts ? "border border-danger" : ""}
                            label=""
                            hideLabel={true}
                            isValid={this.state.minimumServicingCounts <= this.state.memberCounts}
                            errorText={"Servicing Minimum Count must be less than Member Count"}
                            value={this.state.minimumServicingCounts ? this.state.minimumServicingCounts : 0}
                            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                            onChange={(e) => {
                              handleMinimumServicingCountsChange(e.target.value);
                            }}
                          />
                          <QESTextInput
                            testId={"create-specialty-panel-content__memberCounts"}
                            containerClassName="mb-1 label-s"
                            className={+this.state.memberCounts < +this.state.minimumServicingCounts ? "border border-danger" : ""}
                            label=""
                            hideLabel={true}
                            isValid={this.state.minimumServicingCounts <= this.state.memberCounts}
                            errorText={"Member Count must be more than Servicing Minimum Count"}
                            value={this.state.memberCounts ? this.state.memberCounts : 0}
                            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                            onChange={(e) => {
                              handleMemberCountsChange(e.target.value);
                            }}
                          />
                        </>
                      }
                    </div>
                  </div>
                </div>
                <div className="p-1 bd-highlight">
                  <QESSelect
                    testId="create-manage-specialtyGroup-panel-content__countDistinct"
                    containerClassName="mb-1 ml-1 label-s"
                    label="Count Distinct"
                    className={(this.state.countDistinct === "unknown" || this.state.countDistinct.length ===0) && this.state.highlightErrors ? "border border-danger" : ""}
                    errorText={"Count Distinct must be selected"}
                    value={this.state.countDistinct}
                    required={true}
                    isValid={this.state.countDistinct.length != 0 && this.state.countDistinct != "unknown"}
                    onChange={(e) => {
                      this.setState({ countDistinct: e.target.value as ServicingCountTypeEnum });
                    }}
                  >
                    {Object.values(ServicingCountTypeEnum).filter(x => x !== ServicingCountTypeEnum.Unknown).map((x, index) => <option key={index} value={x}>{x}</option>)}
                  </QESSelect>
                </div>
              </div>
              {this.state.servicingDetailsErrorMessages.length > 0 && this.state.highlightErrors && (
                <div className="p-1 bd-highlight my-2">
                  {getErrorMessages()}
                </div>
              )}
              <div className="header-s my-4">
                Template Counts
              </div>
              {/* Waffles Servicing Details */} {this.state.servicingDetailsData.length > 0 &&
              <div className="d-flex flex-row">
                <div className="p-1 bd-highlight">
                  <Waffles<ServicingDetailsRow>
                    columns={this.state.skipAccessTimeAndDistanceCalc === true ? classificationSkipAccessCheckedColumnConfig : classificationColumnConfig}
                    data={this.state.servicingDetailsData}
                    enablePagination={true}
                    fullWidth={true}
                    loading={this.state.loading}
                    testId="client-manage-specialtyGroupServicingDetails-waffles-table" />
                </div>
              </div>}
              <div>
                <QESButton
                  qesTestId={`add-specialty__access-customization__add-button`}
                  qesType="secondary"
                  onClick={this.handleAddAccessCustomization}
                  disabled={this.state.accessCustomizations.length > 0 &&
                    !this.state.accessCustomizations.map((x) => x.geographyId).every((x) => !!x)}
                >
                  Add Access Customization
                </QESButton>
              </div> {this.state.accessCustomizationsData.length > 0 &&
              <div className="d-flex flex-row">
                <div className="p-1 bd-highlight">
                  <Waffles<ServicingDetailsRow>
                    columns={this.state.skipAccessTimeAndDistanceCalc === true ? customizationSkipAccessCheckedColumnConfig : customizationColumnConfig}
                    data={this.state.accessCustomizationsData}
                    enablePagination={true}
                    fullWidth={true}
                    loading={this.state.loading}
                    testId="client-manage-specialtyGroupAccessCustomization-waffles-table" />
                </div>
              </div>}
            </div>

            <FlyoutFooter
              submitButton={<QESButton
                qesTestId="create-manage-specialtyGroup-panel-content__confirm"
                className="mt10"
                onClick={this.handleAddClick}
              >Save Specialty</QESButton>
              }
              cancelButtonText={"Cancel"}
            />
          </>
        }
      </>
    );
  }
}

const CreateNewSpecialtyPanelContent = withFlyoutContext(WrappedCreateNewSpecialtyPanelContent);
export default CreateNewSpecialtyPanelContent;